import { inject, nextTick, App, reactive, markRaw } from "vue";

import YekongaDatabase from './database'

declare global {
    interface Window {
        localeUpdated: boolean;
        systemLanguages: any[];
        systemDefaultLanguage: any[];
    }
}

interface TranslateOptions {
    locale?: string;
    fallbackLocale?: string;
    messages?: Record<string, any>;
    silentTranslationWarn?: boolean;
}

interface TranslationItem {
    uuid?: string;
    locale: string;
    moduleName: string;
    group: string;
    item: string;
    text: string;
    data?: any;
}

const isEmpty = function(value: any) {
    if (
        value === null || 
        typeof value === 'undefined' || 
        (typeof value === 'string' && value.trim() == '')
    ) {
        return true;
    }

    return false;
}

const toTitle = function(text: any) {
    if(!text) return '';
    
    var t = text
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capital letters (camelCase support)
        .toLowerCase()                       // Convert to lowercase
        .trim()                              // Remove leading/trailing spaces
        .replace(/[^a-z0-9\s-]/g, '')        // Remove all non-word chars except spaces and hyphens
        .replace(/\s+/g, '-')                // Replace spaces with hyphens
        .replace(/-+/g, '-')                 // Replace multiple hyphens with single
        .replace(/-/g, ' ');

    return t.substring(0,1).toUpperCase() + t.substring(1)
}

class Translate {
    Database: YekongaDatabase;
    processing: boolean;
    missingTranslations: Record<string, { 
        locale: string[] | Record<string, string | undefined>; 
        moduleName: string; 
        group: string | null; 
        name: string | null;
        text: string | null;
        description: string | null;
    }> | null;
    options: TranslateOptions;
    isInitialized: boolean;
    $app: App;

    // Reactive state
    state: {
        currentLocale: string;
        messages: Record<string, any>;
    };

    constructor(options: TranslateOptions = { locale: 'en' }, app: App) {
        this.processing = false;
        this.missingTranslations = null;
        
        this.state = reactive({
            currentLocale: options.locale || 'en',
            messages: {}
        });

        this.options = {
            locale: 'en',
            fallbackLocale: 'en',
            messages: {},
            silentTranslationWarn: true,
            ...options,
        };
        this.isInitialized = false;
        this.$app = app;
        this.Database = new YekongaDatabase('systemLanguages', {
            version: 1,
            tables: [
                { name: "languages", key: "uuid" },
                { name: "translations", key: "uuid" },
            ],
        });

        this.getLanguages();
    }

    getTrans(_trans: Translate) {
        const $locale = _trans;

        return function (key?: string | undefined| null, args?: { [key: string]: any } | null | string): string {
            var defaultValue = null;

            if(typeof args == 'string') {
                defaultValue = args;
            }
            
            let value = defaultValue ?? key ?? '-';
            
            // Access reactive state to trigger updates
            if ($locale && key) {
                const currentLocale = $locale.state.currentLocale;
                const messages = $locale.state.messages[currentLocale];
                
                // Fallback handling
                const fallback = $locale.options.fallbackLocale ?? 'en';
                const fallbackMessages = $locale.state.messages[fallback];

                if(messages && messages[key]) {
                    value = messages[key];
                } else if(fallbackMessages && fallbackMessages[key]) {
                    value = fallbackMessages[key];
                } else {
                    var t = key.split('.').pop();
                    value = toTitle(t || key);
                    // Only log missing if we actually have messages loaded for this locale
                    // preventing premature missing logs during load
                    if (messages) {
                        $locale.missing(currentLocale, key);
                    }
                }
            }

            // Simple Interpolation
            if (typeof args === 'object' && args !== null && value && typeof value === 'string') {
                Object.keys(args).forEach(k => {
                   const replaceVal = (args as any)[k];
                   value = value.replace(new RegExp(`{${k}}`, 'g'), replaceVal);
                });
            }

            return value;
        }
    }

    async getLanguages(): Promise<void> {
        let response: any = null;
        const separator = !(window.$baseUrl && window.$baseUrl.endsWith("/")) ? '/' : '';
        const url = `${window.$baseUrl}${separator}auth/languages`;
        
        try {
            response = await window.$ajaxGet(url);
            window.$consoleLog("Languages response", response);

            if(Array.isArray(response) && response.length > 0) {  
                window.systemLanguages = response;
                await this.setDefaultLanguage();
            } else {
                // Try creating default if missing (logic preserved from original)
                await this.createLanguage('en', 'English', 'us');
                response = await window.$ajaxGet(url);
                if(Array.isArray(response) && response.length > 0) {  
                    window.systemLanguages = response;
                    await this.setDefaultLanguage();
                }
            }
        } catch (e) {
            console.error("Failed to get languages", e);
        }

        setInterval(() => this.processMissing(), 5000);
    }

    async createLanguage(locale: string, name: string, flag: string = ""): Promise<void> {
        let response: any = null;
        const separator = !(window.$baseUrl && window.$baseUrl.endsWith("/")) ? '/' : '';
        const url = `${window.$baseUrl}${separator}auth/graphql`;   
        const query = `mutation ($input:TranslatorLanguageInput!) {result:createTranslatorLanguage(input:$input){status,message}}`;
        response = await window.$ajaxGraphql(query, { input: { locale, name, flag, status: "active" } }, url);
        window.$consoleLog(response);
    }

    async setLocale(locale: string, messages?: any, app: App | null = null, force: boolean = false): Promise<void> {

        window.$consoleLog(`Translations for ${locale}`, this.state.messages);
        if (isEmpty(locale)) return;

        // 1. If not loaded or forced, load translations
        if (!this.state.messages[locale] || force) {
            let data = await this.getLanguageTranslations(locale); // From DB

            if (!data || force) {
                let response: any = null;
                const separator = !(window.$baseUrl && window.$baseUrl.endsWith("/")) ? '/' : '';
                const url = `${window.$baseUrl}${separator}auth/translations/${locale}`;
                
                try {
                    response = await window.$ajaxGet(url);
                    data = response;
                    window.$consoleLog(`Loaded translations for ${locale}`, data);

                    if(Array.isArray(data)) {
                        await this.setLanguageTranslations(locale, data); // Save to DB
                    }
                } catch (e) {
                    window.$consoleError(`Failed to load translations for ${locale}`, e);
                }
            } else {
                window.$consoleLog(`Existing translations for ${locale}`, data);
            }

            if(Array.isArray(data)) {
                // Process and store
                const processed = this.processData(data);
                // Use markRaw for performance, we don't need deep reactivity on the dictionary
                this.state.messages[locale] = markRaw(processed); 
            }
        }

        // 2. Set current language (Logic separated so we can switch even if cached)
        if (this.state.messages[locale]) {
            this.state.currentLocale = locale;
            window.localStorage.setItem('locale', locale);
            document.querySelector('html')?.setAttribute('lang', locale);
            
            this.isInitialized = true;
            window.localeUpdated = true;
            setTimeout(() => window.localeUpdated = false, 1500);
        }
    }

    async setDefaultLanguage(): Promise<void> {
        try {
            const locale = window.localStorage.getItem('locale') || 'en';
            if (Array.isArray(window.systemLanguages) && window.systemLanguages.length) {
                await this.Database.table('languages').clear();

                for (const row of window.systemLanguages) {
                    await this.Database.table('languages').create(row);
                }
            }

            // Pre-load default language into state if available in window
            if (Array.isArray(window.systemDefaultLanguage) && window.systemDefaultLanguage.length) {
                const processed = this.processData(window.systemDefaultLanguage);
                this.state.messages[locale] = markRaw(processed);
                
                // Also update DB
                await this.Database.table('translations').clear();
                await this.setLanguageTranslations(locale, window.systemDefaultLanguage);
            }

            await this.setLocale(locale);
        } catch (error) {
            console.warn(error);
        }

    }

    async setLanguageTranslations(locale: string, data: any): Promise<void> {
        try {
            const body: { uuid: string; locale: string; data: any } = { uuid: locale, locale, data };
            const exists = await this.Database.table('translations').where('locale', locale).findOne();
            if (!exists) {
                await this.Database.table('translations').create(body);
            } else {
                await this.Database.table('translations').update(body);
            }
        } catch (error) {
            console.warn(error);
        }
    }

    async getLanguageTranslations(locale: string): Promise<any> {
        let data: { uuid?: string; locale?: string; data?: any } = {};
        try {
            if (this.Database) {
                let d = await this.Database.table<typeof data>('translations').where('locale', locale).findOne();
                if (d) {
                    data = d;
                }
            }
        } catch(e) { }

        return data?.data ?? null;
    }

    processData(data: TranslationItem[]): Record<string, any> {
        const values: Record<string, any> = {};
        if(Array.isArray(data)) {
            for (const item of data) {
                var keys = [];
                
                if(!isEmpty(item.moduleName) && item.moduleName != "*") {
                    keys.push(item.moduleName);
                } if(!isEmpty(item.group)) {
                    keys.push(item.group);
                } if(!isEmpty(item.item)) {
                    keys.push(item.item);
                }
                
                var key = keys.join(".");
                values[key] = item.text;
            }
        }
        return values;
    }

    missing(locale: string, key: string): void {
        if (this.processing) return;
        console.dev(`Missing translation key ${key} for ${locale}`);
        const arrayData = key.split('.');
        if (arrayData.length < 2) return; // Basic validation

        const name = arrayData.pop() ?? '';
        const group = arrayData.pop() ?? '';
        const moduleName = arrayData.pop() ?? '';
        const text = toTitle(name);

        this.missingTranslations = this.missingTranslations ?? JSON.parse(window.localStorage.getItem('missingTranslations') || '{}') ?? {};
        if(this.missingTranslations) {
            if (group && name && group.trim() !== "" && name.trim() !== "") {
                const data = this.missingTranslations[key] ?? { 
                    locale: [], 
                    moduleName: moduleName || "*", 
                    description: text,
                    group, 
                    name,
                    text,
                };

                if (Array.isArray(data.locale) && !data.locale.includes(locale)) {
                    data.locale.push(locale);
                }
                this.missingTranslations[key] = data;
            }
        }
        window.localStorage.setItem('missingTranslations', JSON.stringify(this.missingTranslations));
    }

    async processMissing(): Promise<void> {
        if (this.processing ) return;
        this.missingTranslations = this.missingTranslations ?? JSON.parse(window.localStorage.getItem('missingTranslations') || '{}') ?? {};
        
        // Quick check if empty
        if (Object.keys(this.missingTranslations || {}).length === 0) return;

        this.processing = true;
        const body: { 
            group: string; item: string; moduleName: string; 
            locale: string; description: any; text: any 
        }[] = [];

        for (const key in this.missingTranslations) {
            const item = this.missingTranslations[key];
            if (Array.isArray(item.locale)) {
                for (const locale of item.locale) {
                    body.push({ 
                        group: item.group!, item: item.name!, moduleName: item.moduleName, 
                        description: item.description, text: item.text, locale 
                    });
                }
            } else {
                for (const locale in item.locale) {
                    // Handle case where locale might be an object/dict
                    body.push({ 
                        group: item.group!, item: item.name!, moduleName: item.moduleName, 
                        description: item.description, text: item.text, locale 
                    });
                }
            }
        }

        if (body.length) {
            // window.$consoleDebug(`Update missing translations keys: (${body.length})`);
            
            const separator = !(window.$baseUrl && window.$baseUrl.endsWith("/")) ? '/' : '';
            const url = `${window.$baseUrl}${separator}auth/graphql`;

            try {
                const query = `mutation ($input:[TranslatorTranslationInput!]) {result:importTranslatorTranslations(input:$input){status,message}}`;
                const response = await window.$ajaxGraphql(query, { input: body }, url);
                // window.$consoleDebug(response);

                if (response && response.result?.status) {
                    this.missingTranslations = {};
                    window.localStorage.setItem('missingTranslations', '{}');
                    // Reload current locale to get new keys
                    this.setLocale(this.state.currentLocale, null, null, true);
                }
            } catch (error) {
                console.warn("Failed to report missing translations", error);
            }
        }
        setTimeout(() => this.processing = false, 5000);
    }
}

const TRANSLATE_KEY = "$translate";
const TRANSLATE_LOCALE = "$locale";
export const SUPPORT_LOCALES: string[] = Array.isArray(window.systemLanguages) ? window.systemLanguages.map((e: any) => e.locale) : ['en', 'sw'];

export function createLocale(options: TranslateOptions) {
    return {
        install: (app: App, opt?: any): void => {
            const translate = new Translate(options, app);

            app.config.globalProperties.$t = translate.getTrans(translate);
            app.config.globalProperties.$locale = translate;

            app.provide(TRANSLATE_KEY, translate.getTrans(translate));
            app.provide(TRANSLATE_LOCALE, translate);
        },
    };
} 

export function useTranslate(): (
    name:string | undefined | null, 
    args?: { [key: string]: any } | null | string | undefined
) => string {
    try {
        const value = inject<(
            name:string | undefined | null, 
            args?: { [key: string]: any } | null | string | undefined
        ) => string>(TRANSLATE_KEY);
        if (!value) throw new Error("No translate function provided");
    
        return value;
    } catch (error) {
        console.error("useTranslate.error", error);
    }

    return (name:string | undefined | null, args?: { [key: string]: any } | null | string | undefined): string => {
        if(!name) return '';
        // Simple translation function placeholder
        // In a real app, integrate with a proper i18n library
        return name;
    }
}

export function useLocale(): Translate {
    const value = inject<Translate>(getLocaleKey());
    if (!value) throw new Error("No locale provided");
    return value;
}

export function getLocaleKey(): string {
    return TRANSLATE_LOCALE;
}
